import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {P} from 'UI/components/layout/Text';

/** Values longer than this are considered "long descriptions" and span both grid columns on desktop. */
const LONG_VALUE_THRESHOLD = 28;

/** A single stat tile definition. */
export interface StatTile {
    /** The icon element rendered above the label. */
    icon: ReactNode;
    /** Unique key for the tile. */
    key: string;
    /** The uppercase label (e.g. "DISTANCE"). */
    label: string;
    /** The bold value (e.g. "3.2km"). */
    value: string;
}

/**
 * The props for the `StatTileGrid` component.
 * Configures a 2×2 bento-style grid of stat tiles.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'StatTileGrid'
     */
    testId?: string;
    /** Array of stat tile definitions to render (max 4 recommended). */
    tiles: StatTile[];
}

/**
 * A 2×2 bento-style grid of stat tiles, each displaying an icon, uppercase label, and bold value.
 * Matches the Stitch design's "Features Grid / Stat Tiles" section with surface-container-low backgrounds.
 *
 * @param props        The component props.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'StatTileGrid'`.
 * @param props.tiles  Array of stat tile definitions.
 * @returns A JSX element rendering the StatTileGrid.
 *
 * @example
 * ```tsx
 * <StatTileGrid tiles={[
 *     {key: 'distance', icon: <RouteIcon />, label: 'DISTANCE', value: '3.2km'},
 *     {key: 'duration', icon: <ScheduleIcon />, label: 'DURATION', value: '1h 15m'},
 * ]} />
 * ```
 */
const StatTileGrid = ({testId = 'StatTileGrid', tiles}: ComponentProps) => (
    <Grid data-cy={testId}>
        {tiles.map(tile => (
            <Tile
                key={tile.key}
                $wide={tile.value.length > LONG_VALUE_THRESHOLD}
                data-cy={`${testId}-tile-${tile.key}`}
            >
                <IconWrapper>{tile.icon}</IconWrapper>
                <Label $size="labelMedium" as="span">{tile.label}</Label>
                <Value $size="bodySmall" as="span">{tile.value}</Value>
            </Tile>
        ))}
    </Grid>
);

StatTileGrid.displayName = 'StatTileGrid';

export {StatTileGrid};

// ── Styled ──────────────────────────────────────────────────

const Grid = styled.section`
    display: grid;
    gap: ${spacing(4)};
    grid-template-columns: repeat(2, 1fr);
`;

const Tile = styled.div<{$wide: boolean}>`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceLow};
    border-radius: ${spacing(5)};
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    padding: ${spacing(6)} ${spacing(4)};
    text-align: center;

    ${media('lg')} {
        grid-column: ${({$wide}) => ($wide ? 'span 2' : 'auto')};
    }
`;

const IconWrapper = styled.div`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-size: 2.8rem;
    line-height: 1;
    margin-bottom: ${spacing(1)};
`;

const Label = styled(P)`
    color: ${({theme}) => theme.colors.inkTertiary};
    text-align: center;
`;

const Value = styled(P)`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-weight: 700;
    text-align: center;
`;